import * as Sentry from '@sentry/react';
import { Table } from '@tanstack/react-table';
import * as _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import {
  Bank,
  CreditAgreement,
  FundingRequest,
  Util,
} from '@finley-technologies/finley-types';

import {
  BankAccountCashBalanceGQLType,
  BorrowingBaseReportGQLType,
  BorrowingBaseTestTypeGQLType,
  ConcentrationLimitCalculationGQLType,
  EligibilityCriteriaCalculationGQLType,
  ReceivableBorrowingBaseTestWaiverGQLType,
  ReceivableBorrowingBaseTestWaiverInputGQLType,
  ReceivableIdGroupWithLoanProductGQLType,
  VerificationBorrowerLoanProductCalculationGQLType,
  VerificationBorrowerParsedBorrowingBaseReportGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import {
  isSanitizedJSON,
  isSanitizedJSONWithNumberValues,
} from './verificationTablesUtils';

export const waiverImpactHeaderElementId = 'verification-waiver-impact-header';

/**
 * returns the earliest reporting date borrowing base report that is not from the invalidReportingDate
 */
export const getEarliestValidReferenceBorrowingBaseReport = ({
  borrowingBaseReports,
  invalidReportingDate,
}: {
  borrowingBaseReports: BorrowingBaseReportGQLType[];
  invalidReportingDate: Util.FullDateStr;
}): BorrowingBaseReportGQLType | undefined => {
  if (!borrowingBaseReports.length) {
    return undefined;
  }
  const borrowingBaseReportsSortedByReportingDateDesc = _.orderBy(
    borrowingBaseReports,
    'reportingDate',
    'desc',
  );
  const earliestBorrowingBaseReportFromPreviousWeek = _.last(
    borrowingBaseReportsSortedByReportingDateDesc,
  );

  if (
    earliestBorrowingBaseReportFromPreviousWeek?.reportingDate ===
    invalidReportingDate
  ) {
    return undefined;
  }

  return earliestBorrowingBaseReportFromPreviousWeek;
};

const parseBorrowerValueOrZeroFromVerificationReport = ({
  sanitizedJSON,
  field,
}: {
  sanitizedJSON: Util.SanitizedJSON;
  field: string;
}): number => {
  const valuesForField = sanitizedJSON[field];
  if (!isSanitizedJSONWithNumberValues(valuesForField)) {
    return 0;
  }

  return _.isNumber(valuesForField.borrowerValue)
    ? valuesForField.borrowerValue
    : 0;
};

const parseBorrowerValueOrNullFromVerificationReport = ({
  sanitizedJSON,
  field,
  verificationReportTranslationFieldName,
}: {
  sanitizedJSON: Util.SanitizedJSON;
  field: string;
  verificationReportTranslationFieldName?: string; // during the interim migration, borrowingBaseAmountAssets need to be migrated and renamed
}): number | null => {
  const valuesForField =
    sanitizedJSON[verificationReportTranslationFieldName ?? field];

  if (!isSanitizedJSONWithNumberValues(valuesForField)) {
    return null;
  }

  return _.isNumber(valuesForField.borrowerValue)
    ? valuesForField.borrowerValue
    : null;
};

const parseVerificationReportEligibilityCriteriaCalculations = ({
  verificationReport,
}: {
  verificationReport: Util.SanitizedJSON;
}): EligibilityCriteriaCalculationGQLType[] => {
  const verificationReportEligibilityCriteriaCalculations =
    verificationReport.eligibilityCriteriaCalculations;
  if (
    !verificationReportEligibilityCriteriaCalculations ||
    !isSanitizedJSON(verificationReportEligibilityCriteriaCalculations)
  ) {
    return [];
  }

  // create placeholder uuid to adhere to the standard EligibilityCriteriaCalculation typedef
  const placeholderBorrowingBaseReportId = uuidv4();
  const placeholderCreatedAt = commonDate.currentEpochMs();

  return _.compact(
    _.map(
      verificationReportEligibilityCriteriaCalculations,
      (calc, eligibilityCriteriaKey) => {
        if (
          !calc ||
          !isSanitizedJSON(calc) ||
          !calc.eligibilityReduction ||
          !isSanitizedJSONWithNumberValues(calc.eligibilityReduction)
        ) {
          return null;
        }

        const placeholderEligibilityCriteriaId = uuidv4();
        const eligibilityReduction =
          calc.eligibilityReduction.borrowerValue ?? 0;

        return {
          id: placeholderEligibilityCriteriaId,
          borrowingBaseReportId: placeholderBorrowingBaseReportId,
          eligibilityCriteriaKey,
          eligibilityReduction,
          created_at: placeholderCreatedAt,
        };
      },
    ),
  );
};

/**
 * Given a credit agreement, returns a map of concentration limit keys to loan product slugs
 * in the following map:
 * {
 *   concentrationLimitKey: [lpSlug1, lpSlug2],
 *   concentrationLimitKey2: [lpSlug1]
 * }
 */
const parseConcentrationLimitKeyToLoanProductSlugsMap = ({
  creditAgreement,
}: {
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
}): {
  [concentrationLimitKey: string]: string[];
} => {
  const {
    borrowingBaseMechanics: {
      calcAgentMechanics: {
        loanProductMechanics,
        multiLoanProductConcentrationLimits,
      },
    },
  } = creditAgreement;

  const concentrationLimitKeyToLoanProductSlugsMap: {
    [concentrationLimitKey: string]: string[];
  } = {};

  _.forEach(loanProductMechanics, (lpMechanic) => {
    const loanProductSlug = lpMechanic.entity.slug;

    _.forEach(lpMechanic.concentrationLimits, (lpConcLimit) => {
      const lpConcLimitPropertyKey =
        lpConcLimit._config_?.concentrationLimitConfig?.propertyKey;

      if (lpConcLimitPropertyKey) {
        if (
          !concentrationLimitKeyToLoanProductSlugsMap[lpConcLimitPropertyKey]
        ) {
          concentrationLimitKeyToLoanProductSlugsMap[
            lpConcLimitPropertyKey
          ] = [];
        }

        concentrationLimitKeyToLoanProductSlugsMap[lpConcLimitPropertyKey].push(
          loanProductSlug,
        );
      }
    });
  });

  _.forEach(multiLoanProductConcentrationLimits, (mlpConcLimit) => {
    const mlpConcLimitPropertyKey =
      mlpConcLimit._config_?.concentrationLimitConfig?.propertyKey;
    if (mlpConcLimitPropertyKey) {
      const loanProductSlugs = _.map(
        mlpConcLimit.loanProducts,
        (lp) => lp.entity.slug,
      );

      concentrationLimitKeyToLoanProductSlugsMap[
        mlpConcLimitPropertyKey
      ] = loanProductSlugs;
    }
  });

  return concentrationLimitKeyToLoanProductSlugsMap;
};

/**
 * Returns the total eligible exposure for the given loan product slugs from a verification report JSON
 */
const parseTotalEligibleExposureForLoanProductSlugs = ({
  verificationReport,
  loanProductSlugs,
}: {
  verificationReport: Util.SanitizedJSON;
  loanProductSlugs: string[];
}): number => {
  const loanProductCalculationsPrefix = 'loanProductCalculations';
  const totalEligibleExposureField = 'totalEligibleExposure';

  const verificationReportLoanProductCalculations =
    verificationReport[loanProductCalculationsPrefix];

  if (!isSanitizedJSON(verificationReportLoanProductCalculations)) {
    return 0;
  }

  const eligibleExposuresForLoanProducts = _.map(loanProductSlugs, (lpSlug) => {
    const verificationReportLpCalcsForLoanProduct =
      verificationReportLoanProductCalculations[lpSlug];

    if (!isSanitizedJSON(verificationReportLpCalcsForLoanProduct)) {
      return 0;
    }

    const totalEligibleExposureForLoanProduct = parseBorrowerValueOrZeroFromVerificationReport(
      {
        sanitizedJSON: verificationReportLpCalcsForLoanProduct,
        field: totalEligibleExposureField,
      },
    );

    return totalEligibleExposureForLoanProduct;
  });

  return _.sum(eligibleExposuresForLoanProducts);
};

const parseVerificationReportConcentrationLimitCalculations = ({
  verificationReport,
  creditAgreement,
}: {
  verificationReport: Util.SanitizedJSON;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
}): ConcentrationLimitCalculationGQLType[] => {
  const verificationReportConcentrationLimitCalculations =
    verificationReport.concentrationLimitCalculations;
  if (
    !verificationReportConcentrationLimitCalculations ||
    !isSanitizedJSON(verificationReportConcentrationLimitCalculations)
  ) {
    return [];
  }

  // create placeholder uuid to adhere to the standard ConcentrationLimitCalculation typedef
  const placeholderBorrowingBaseReportId = uuidv4();
  const placeholderCreatedAt = commonDate.currentEpochMs();

  const concentrationLimitKeyToLoanProductSlugsMap = parseConcentrationLimitKeyToLoanProductSlugsMap(
    { creditAgreement },
  );

  return _.compact(
    _.map(
      verificationReportConcentrationLimitCalculations,
      (calc, concentrationLimitKey) => {
        if (!calc || !isSanitizedJSON(calc)) {
          return null;
        }

        // placeholders to conform to type, will not be used
        const placeholderConcentrationLimitId = uuidv4();
        const placeholderComplianceStatus = 'COMPLIANT';

        const loanProductSlugsForConcentrationLimit =
          concentrationLimitKeyToLoanProductSlugsMap[concentrationLimitKey];

        if (!loanProductSlugsForConcentrationLimit) {
          return null;
        }

        const totalEligibleExposureForConcentrationLimit = parseTotalEligibleExposureForLoanProductSlugs(
          {
            verificationReport,
            loanProductSlugs: loanProductSlugsForConcentrationLimit,
          },
        );

        return {
          id: placeholderConcentrationLimitId,
          borrowingBaseReportId: placeholderBorrowingBaseReportId,
          concentrationLimitKey,
          concentrationLimit: parseBorrowerValueOrZeroFromVerificationReport({
            sanitizedJSON: calc,
            field: 'concentrationLimit',
          }),
          concentrationAmount: parseBorrowerValueOrNullFromVerificationReport({
            sanitizedJSON: calc,
            field: 'concentrationAmount',
          }),
          totalEligibleExposure: totalEligibleExposureForConcentrationLimit,
          concentration: parseBorrowerValueOrZeroFromVerificationReport({
            sanitizedJSON: calc,
            field: 'concentration',
          }),
          borrowingBaseReduction: parseBorrowerValueOrZeroFromVerificationReport(
            {
              sanitizedJSON: calc,
              field: 'borrowingBaseReduction',
            },
          ),
          complianceStatus: placeholderComplianceStatus,
          created_at: placeholderCreatedAt,
        };
      },
    ),
  );
};

export const isCurrency = (test: any): test is Bank.Currency => {
  const isValid = _.includes(_.values(Bank.Currency), test);

  if (!isValid) {
    return false;
  }

  return true;
};

const parseVerificationReportBankAccountCashBalances = ({
  verificationReport,
}: {
  verificationReport: Util.SanitizedJSON;
}): BankAccountCashBalanceGQLType[] => {
  const verificationReportBankAccountCashBalances =
    verificationReport.bankAccountCashBalances;
  if (
    !verificationReportBankAccountCashBalances ||
    !isSanitizedJSON(verificationReportBankAccountCashBalances)
  ) {
    return [];
  }

  return _.compact(
    _.map(
      verificationReportBankAccountCashBalances,
      (accountDetails, accountType) => {
        if (!accountDetails || !isSanitizedJSON(accountDetails)) {
          return null;
        }

        // create placeholder bank account id to adhere to BankAccountCashBalance typedef
        const placeholderBankAccountId = uuidv4();

        return {
          bankAccountId: placeholderBankAccountId,
          currencyCode: 'USD',
          conversionRate: 1,
          balance: parseBorrowerValueOrZeroFromVerificationReport({
            sanitizedJSON: accountDetails,
            field: 'balance',
          }),
          accountType,
          cashAdvanceRate: parseBorrowerValueOrNullFromVerificationReport({
            sanitizedJSON: accountDetails,
            field: 'cashAdvanceRate',
          }),
          currency:
            isSanitizedJSON(accountDetails.currency) &&
            isCurrency(accountDetails.currency.borrowerValue)
              ? accountDetails.currency.borrowerValue
              : null,
        };
      },
    ),
  );
};

const parseVerificationReportLoanProductCalculations = ({
  verificationReport,
  creditAgreement,
}: {
  verificationReport: Util.SanitizedJSON;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
}): VerificationBorrowerLoanProductCalculationGQLType[] => {
  const verificationReportLoanProductCalculations =
    verificationReport.loanProductCalculations;
  if (
    !verificationReportLoanProductCalculations ||
    !isSanitizedJSON(verificationReportLoanProductCalculations)
  ) {
    return [];
  }

  const {
    borrowingBaseMechanics: {
      calcAgentMechanics: { loanProductMechanics },
    },
    creditFacilityId,
    id: creditAgreementId,
  } = creditAgreement;

  // create placeholder uuid to adhere to BorrowingBaseReportLoanProductCalculation typedef
  const placeholderBorrowingBaseReportId = uuidv4();

  return _.compact(
    _.map(
      verificationReportLoanProductCalculations,
      (loanProductCalculations, loanProductSlug) => {
        if (
          !loanProductCalculations ||
          !isSanitizedJSON(loanProductCalculations)
        ) {
          return null;
        }

        const loanProduct = _.find(
          loanProductMechanics,
          (lp) => lp.entity.slug === loanProductSlug,
        );

        if (!loanProduct) {
          Sentry.captureMessage(
            `Loan product slug configured in xlsx parsing does not match any loan products in the credit agreement. Searched for [loanProductSlug: ${loanProductSlug}] in [creditAgreementId: ${creditAgreementId}]`,
          );
          return null;
        }

        const loanProductCalculationsBasefields: (keyof Omit<
          VerificationBorrowerLoanProductCalculationGQLType,
          | '__typename'
          | 'borrowingBaseReportId'
          | 'creditFacilityId'
          | 'loanProductId'
        >)[] = [
          'totalReceivableExposure',
          'totalEligibilityKickout',
          'totalEligibleExposure',
          'totalConcentrationKickout',
          'totalEligibleExposureLessConcentrationKickouts',
          'weightedAverageReceivableAdvanceRate',
          'totalReceivableBorrowingBaseAmount',
          'effectiveReceivableAdvanceRate',
          'effectiveReceivableValuationRate',
        ];

        const verificationLoanProductCalculations: VerificationBorrowerLoanProductCalculationGQLType = {
          borrowingBaseReportId: placeholderBorrowingBaseReportId,
          creditFacilityId,
          loanProductId: loanProduct.entity.id,

          totalReceivableExposure: 0,
          totalEligibilityKickout: 0,
          totalEligibleExposure: 0,
          totalConcentrationKickout: 0,
          totalEligibleExposureLessConcentrationKickouts: 0,
          weightedAverageReceivableAdvanceRate: 0,
          totalReceivableBorrowingBaseAmount: 0,
          effectiveReceivableAdvanceRate: 0,
          effectiveReceivableValuationRate: 0,
        };

        loanProductCalculationsBasefields.forEach((field) => {
          verificationLoanProductCalculations[
            field
          ] = parseBorrowerValueOrNullFromVerificationReport({
            sanitizedJSON: loanProductCalculations,
            field,
          });
        });

        return verificationLoanProductCalculations;
      },
    ),
  );
};

/**
 * This is a TEMPORARY method to convert the current verification report json to a borrowing base report format.
 * This is done TEMPORARILY here on FE to help with PR sequencing and continuity of the verification report functionality.
 * This will be moved to modules/funding-request during the generation of the verification report as a fast follow.
 * At that time, the funding-request api will return a similar VerificationBorrowingBaseReport and VerificationBorrowingBaseReportLoanProductCalculations instead of this SanitizedJSON for the parsed borrower numbers
 */
export const formatBorrowerVerificationReportJsonToBorrowingBaseReport = ({
  verificationReport,
  creditAgreement,
}: {
  verificationReport: Util.SanitizedJSON;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
}): VerificationBorrowerParsedBorrowingBaseReportGQLType => {
  const verificationBorrowerParsedBorrowingBaseReportFields: (keyof Omit<
    VerificationBorrowerParsedBorrowingBaseReportGQLType,
    | '__typename'
    | 'eligibilityCriteriaCalculations'
    | 'concentrationLimitCalculations'
    | 'loanProductCalculations'
    | 'bankAccountCashBalances'
  >)[] = [
    'commitmentAmount',
    'currentPrincipalBalance',
    'totalReceivableExposure',
    'totalEligibilityKickout',
    'totalEligibleExposure',
    'totalConcentrationKickout',
    'borrowingBaseAmountAssets', // to be renamed 'totalEligibleExposureLessConcentrationKickouts'
    'weightedAverageReceivableAdvanceRate',
    'maxReceivableAdvanceAmount', // to be renmaed totalReceivableBorrowingBaseAmount
    'effectiveReceivableAdvanceRate',
    'totalPreAdvanceCashContribution',
    'effectiveCashAdvanceRate',
    'totalCashBorrowingBaseAmount',
    'totalBorrowingBaseAmount',
    'totalAssets',
    'effectiveAdvanceRate',
    'borrowingBaseReserveAmount',
    'totalCollectionsAccountCash',
  ];

  const verificationBorrowerParsedBorrowingBaseReport: VerificationBorrowerParsedBorrowingBaseReportGQLType = {};

  verificationBorrowerParsedBorrowingBaseReportFields.forEach((field) => {
    verificationBorrowerParsedBorrowingBaseReport[
      field
    ] = parseBorrowerValueOrNullFromVerificationReport({
      sanitizedJSON: verificationReport,
      field,
    });
  });

  return {
    ...verificationBorrowerParsedBorrowingBaseReport,
    eligibilityCriteriaCalculations: parseVerificationReportEligibilityCriteriaCalculations(
      { verificationReport },
    ),
    concentrationLimitCalculations: parseVerificationReportConcentrationLimitCalculations(
      { verificationReport, creditAgreement },
    ),
    bankAccountCashBalances: parseVerificationReportBankAccountCashBalances({
      verificationReport,
    }),
    loanProductCalculations: parseVerificationReportLoanProductCalculations({
      verificationReport,
      creditAgreement,
    }),
  };
};

// might need to tone down on the typeguard if affecting performance
export const isVerificationReceivableWaiverImpact = (
  test: any,
): test is FundingRequest.VerificationReceivableWaiverImpact => {
  return (
    test &&
    typeof test === 'object' &&
    'finleyReceivableId' in test &&
    typeof test.finleyReceivableId === 'string' &&
    'externalReceivableId' in test &&
    typeof test.externalReceivableId === 'string' &&
    'borrowingBaseTestType' in test &&
    typeof test.borrowingBaseTestType === 'string' &&
    'borrowingBaseTestKey' in test &&
    typeof test.borrowingBaseTestKey === 'string'
  );
};

// might need to tone down on the typeguard if affecting performance
export const isVerificationBorrowingBaseWaiverImpact = (
  test: any,
): test is FundingRequest.VerificationBorrowingBaseWaiverImpact => {
  if (!test || typeof test !== 'object') {
    return false;
  }
  return _.every(test, (testItem) => {
    if (testItem) {
      if (!Array.isArray(testItem)) {
        return false;
      }
      return _.every(testItem, (testItemArray) =>
        isVerificationReceivableWaiverImpact(testItemArray),
      );
    }
    return false;
  });
};

// might need to tone down on the typeguard if affecting performance
export const isVerificationBorrowingBaseWaiverImpactData = (
  test: any,
): test is {
  eligibilityWaiverImpact: FundingRequest.VerificationBorrowingBaseWaiverImpact;
  concentrationWaiverImpact: FundingRequest.VerificationBorrowingBaseWaiverImpact;
  receivableResponseTapeReceivableIdGroups: ReceivableIdGroupWithLoanProductGQLType[];
} => {
  if (
    !test ||
    typeof test !== 'object' ||
    !('eligibilityWaiverImpact' in test) ||
    !('concentrationWaiverImpact' in test) ||
    !('receivableResponseTapeReceivableIdGroups' in test)
  ) {
    return false;
  }

  return _.every(test, (testItem, key) => {
    if (testItem && key === 'receivableResponseTapeReceivableIdGroups') {
      return true;
    }
    if (testItem) {
      return isVerificationBorrowingBaseWaiverImpact(testItem);
    }
    return false;
  });
};

export type EligibilityWaiverImpactForKey = {
  totalWaiverImpact: number;
  waiverImpacts: FundingRequest.VerificationReceivableWaiverImpact[];
};
export type EligibilityWaiverImpactAggregate = {
  [testKey: string]: EligibilityWaiverImpactForKey;
};

export const generateTotalEligibilityWaiverImpactAndCsvDataRows = ({
  eligibilityWaiverImpact,
}: {
  eligibilityWaiverImpact: FundingRequest.VerificationBorrowingBaseWaiverImpact;
}): {
  totalEligibilityWaiverImpact: number;
  csvDataRows: {
    [key: string]: any;
  }[];
  eligibilityWaiverImpactByTestKey: EligibilityWaiverImpactAggregate;
} => {
  const csvDataRows: { [key: string]: any }[] = [];
  const eligibilityWaiverImpactByTestKey: EligibilityWaiverImpactAggregate = {};

  const totalEligibilityWaiverImpact: number = _.reduce(
    eligibilityWaiverImpact,
    (acc, receivableWaiverImpact) => {
      // push to csv data
      _.forEach(receivableWaiverImpact, (waiverImpact) => {
        csvDataRows.push(waiverImpact);

        const { eligibilityKickoutWaived, borrowingBaseTestKey } = waiverImpact;
        if (!eligibilityWaiverImpactByTestKey[borrowingBaseTestKey]) {
          eligibilityWaiverImpactByTestKey[borrowingBaseTestKey] = {
            totalWaiverImpact: eligibilityKickoutWaived ?? 0,
            waiverImpacts: [waiverImpact],
          };
        } else {
          eligibilityWaiverImpactByTestKey[
            borrowingBaseTestKey
          ].totalWaiverImpact += eligibilityKickoutWaived ?? 0;

          eligibilityWaiverImpactByTestKey[
            borrowingBaseTestKey
          ].waiverImpacts.push(waiverImpact);
        }
      });
      // we only count the waived eligibility once per receivable.
      // if there is at least one eligibility kickout waiver for a given receivable,
      // find one and add its the eligibilityKickoutWaived to the tally
      const eligibilityWaivedForReceivable =
        receivableWaiverImpact[0].eligibilityKickoutWaived;

      if (eligibilityWaivedForReceivable) {
        // eslint-disable-next-line no-param-reassign
        acc += eligibilityWaivedForReceivable;
      }

      return acc;
    },
    0,
  );

  return {
    totalEligibilityWaiverImpact,
    csvDataRows,
    eligibilityWaiverImpactByTestKey,
  };
};

export const concentrationLimitPropertyKeyToConcentrationFormat = (
  calcAgentMechanics: CreditAgreement.DomainEntity.CalcAgentMechanicsWithEntities,
) => {
  const keyToFormatMap: {
    [propertyKey: string]: 'percentage' | undefined;
  } = {};
  _.forEach(
    calcAgentMechanics.multiLoanProductConcentrationLimits,
    (mlpTest) => {
      const propertyKey =
        mlpTest._config_?.concentrationLimitConfig?.propertyKey;
      if (propertyKey) {
        const configType =
          mlpTest._config_?.concentrationLimitConfig?.calculationConfig?.type;
        keyToFormatMap[propertyKey] =
          configType === 'standard' || configType === 'exposureByGroup'
            ? 'percentage'
            : undefined;
      }
    },
  );

  _.forEach(calcAgentMechanics.loanProductMechanics, (loanProductMechanic) => {
    _.forEach(loanProductMechanic.concentrationLimits, (slpTest) => {
      const propertyKey =
        slpTest._config_?.concentrationLimitConfig?.propertyKey;
      if (propertyKey) {
        const configType =
          slpTest._config_?.concentrationLimitConfig?.calculationConfig?.type;
        keyToFormatMap[propertyKey] =
          configType === 'standard' || configType === 'exposureByGroup'
            ? 'percentage'
            : undefined;
      }
    });
  });

  return keyToFormatMap;
};

export const aggregateVerificationBorrowingBaseWaiverImpactTotals = ({
  waiverImpact,
  borrowingBaseReport,
}: {
  waiverImpact: {
    eligibilityWaiverImpact: FundingRequest.VerificationBorrowingBaseWaiverImpact;
    concentrationWaiverImpact: FundingRequest.VerificationBorrowingBaseWaiverImpact;
  };
  borrowingBaseReport: BorrowingBaseReportGQLType;
}): {
  totalEligibilityWaived: number;
  totalConcentrationWaived: FundingRequest.VerificationConcentrationWaiverImpactAggregate[];
} => {
  const defaults = { totalEligibilityWaived: 0, totalConcentrationWaived: [] };

  // if no waiver impacts, return early
  if (
    !_.size(waiverImpact.eligibilityWaiverImpact) &&
    !_.size(waiverImpact.concentrationWaiverImpact)
  ) {
    return defaults;
  }

  const { concentrationLimitCalculations } = borrowingBaseReport;

  const concentrationLimitCalculationsByProperty = _.keyBy(
    concentrationLimitCalculations,
    (concCalc) => concCalc.concentrationLimitKey,
  );

  const { eligibilityWaiverImpact, concentrationWaiverImpact } = waiverImpact;

  const totalEligibilityWaived: number = _.reduce(
    eligibilityWaiverImpact,
    (acc, receivableWaiverImpact) => {
      // we only count the waived eligibility once per receivable
      const eligibilityWaivedForReceivable =
        receivableWaiverImpact[0].eligibilityKickoutWaived;
      if (eligibilityWaivedForReceivable) {
        // eslint-disable-next-line no-param-reassign
        acc += eligibilityWaivedForReceivable;
      }

      return acc;
    },
    0,
  );

  const totalConcentrationAmountWaivedForPropertyKeys: {
    [concentrationPropertyKey: string]: number;
  } = _.reduce(
    concentrationWaiverImpact,
    (acc, receivableWaiverImpact) => {
      // we only count the waived concentration once per receivable
      _.forEach(receivableWaiverImpact, (waiverForReceivable) => {
        const concentrationWaivedForReceivable =
          waiverForReceivable?.eligibleExposureLessConcentrationLimitKickouts;
        if (_.isNumber(concentrationWaivedForReceivable)) {
          if (!acc[waiverForReceivable.borrowingBaseTestKey]) {
            // eslint-disable-next-line no-param-reassign
            acc[waiverForReceivable.borrowingBaseTestKey] = 0;
          }
          // eslint-disable-next-line no-param-reassign
          acc[
            waiverForReceivable.borrowingBaseTestKey
          ] += concentrationWaivedForReceivable;
        }
      });
      return acc;
    },
    {} as {
      [concentrationPropertyKey: string]: number;
    },
  );

  const totalConcentrationWaived: FundingRequest.VerificationConcentrationWaiverImpactAggregate[] = _.map(
    totalConcentrationAmountWaivedForPropertyKeys,
    (concentrationAmountWaived, propertyKey) => {
      const postWaiversConcentrationLimitCalculations =
        concentrationLimitCalculationsByProperty[propertyKey];

      finleyAssert(
        postWaiversConcentrationLimitCalculations,
        `postWaiversConcentrationLimitCalculations for [propertyKey: ${propertyKey}] must exist on [borrowingBaseReportId: ${borrowingBaseReport.id}]`,
      );

      const concentrationAmountNotWaived =
        postWaiversConcentrationLimitCalculations.concentrationAmount;

      const hypotheticalConcentrationAmountIfNoWaivers = _.isNumber(
        concentrationAmountNotWaived,
      )
        ? concentrationAmountNotWaived + concentrationAmountWaived
        : undefined;

      const hypotheticalConcentrationIfNoWaivers = _.isNumber(
        hypotheticalConcentrationAmountIfNoWaivers,
      )
        ? hypotheticalConcentrationAmountIfNoWaivers /
          postWaiversConcentrationLimitCalculations.totalEligibleExposure // use the total eligible exposure that was used to calculate the actual concentration limit from the borrowing base report, since it factors in the correct set of receivables (eg. a single loan product or mlp)
        : undefined;

      return {
        concentrationPropertyKey: propertyKey,
        concentrationAmountWaived,
        concentrationAmountNotWaived,
        concentrationPreWaivers: hypotheticalConcentrationIfNoWaivers,
        concentrationPostWaivers:
          postWaiversConcentrationLimitCalculations.concentration,
        totalEligibleExposure:
          postWaiversConcentrationLimitCalculations.totalEligibleExposure,
      };
    },
  );

  return {
    totalEligibilityWaived,
    totalConcentrationWaived,
  };
};

export const getBorrowerRecentWaiverUpdatesAggregate = ({
  receivableWaivers,
  borrowerOrganizationName,
  reportingDate,
}: {
  receivableWaivers?: ReceivableBorrowingBaseTestWaiverGQLType[];
  borrowerOrganizationName: string;
  reportingDate: Util.FullDateStr;
}) => {
  if (!receivableWaivers || !receivableWaivers.length) {
    return 0;
  }

  const borrowerRecentWaiverUpdatesAggregate = _.reduce(
    receivableWaivers,
    (acc, waiver) => {
      const { updated_at: updatedAt, lastUpdatedOrganizationName } = waiver;
      const isWaiverUpdatedAfterReportingDate = Boolean(
        updatedAt > commonDate.fullDateStrToEpoch(reportingDate),
      );
      const isLastUpdatedByBorrowerOrganization = Boolean(
        borrowerOrganizationName === lastUpdatedOrganizationName,
      );

      if (
        isWaiverUpdatedAfterReportingDate &&
        isLastUpdatedByBorrowerOrganization
      ) {
        // eslint-disable-next-line no-param-reassign
        acc += 1;
      }

      return acc;
    },
    0,
  );

  return borrowerRecentWaiverUpdatesAggregate;
};

export type BorrowerRecentWaiverUpdatesAggregate = {
  [finleyReceivableId: string]: ReceivableBorrowingBaseTestWaiverGQLType;
};

type BorrowerRecentWaiverUpdatesAggregateByKey = {
  [borrowingBaseTestKey: string]: BorrowerRecentWaiverUpdatesAggregate;
};

export const getBorrowerRecentWaiverUpdatesAggregateByTest = ({
  receivableWaivers,
  borrowerOrganizationName,
  reportingDate,
}: {
  receivableWaivers?: ReceivableBorrowingBaseTestWaiverGQLType[];
  borrowerOrganizationName: string;
  reportingDate: Util.FullDateStr;
}) => {
  if (!receivableWaivers) {
    return null;
  }
  const borrowerRecentWaiverUpdatesAggregate = _.reduce(
    receivableWaivers,
    (acc, waiver) => {
      const {
        updated_at: updatedAt,
        lastUpdatedOrganizationName,
        finleyReceivableId,
        borrowingBaseTestKey,
      } = waiver;
      const isWaiverUpdatedAfterReportingDate = Boolean(
        updatedAt > commonDate.fullDateStrToEpoch(reportingDate),
      );
      const isLastUpdatedByBorrowerOrganization = Boolean(
        borrowerOrganizationName === lastUpdatedOrganizationName,
      );

      if (
        isWaiverUpdatedAfterReportingDate &&
        isLastUpdatedByBorrowerOrganization
      ) {
        if (!acc[borrowingBaseTestKey]) {
          // eslint-disable-next-line no-param-reassign
          acc[borrowingBaseTestKey] = {};
        }
        // eslint-disable-next-line no-param-reassign
        acc[borrowingBaseTestKey][finleyReceivableId] = waiver;
      }

      return acc;
    },
    {} as BorrowerRecentWaiverUpdatesAggregateByKey,
  );

  return borrowerRecentWaiverUpdatesAggregate;
};

export const getNumberOfSelectedUniqueWaiverRows = (
  table: Table<ReceivableBorrowingBaseTestWaiverInputGQLType>,
): number => {
  const constraintCombosSet = new Set<string>();

  _.forEach(table.getSelectedRowModel().rows, (row) => {
    const finleyReceivableId: string = row.getValue('finleyReceivableId');
    const borrowingBaseTestType: BorrowingBaseTestTypeGQLType = row.getValue(
      'borrowingBaseTestType',
    );
    const borrowingBaseTestKey: string = row.getValue('borrowingBaseTestKey');

    constraintCombosSet.add(
      `${finleyReceivableId}-${borrowingBaseTestType}-${borrowingBaseTestKey}`,
    );
  });

  return constraintCombosSet.size;
};
